import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
  Rectangle,
  Cell,
} from "recharts";
import {
  getL1SheetProperty,
} from "../../../features/reduxworkbook/WorkbookSlice";
import { useDispatch, useSelector } from "react-redux";
import { getColourSchemeByID } from "../../../Colors/ColorPalettes";
import {
  deleteTemporaryFilters,
  setFiltersToOtherSheets,
} from "../../../features/reduxworkbook/WorkbookSlice";
import { nanoid } from "@reduxjs/toolkit";
import { getSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import { formatNumber } from "../Utils/FormatNumbers";
import { sortData } from "../Utils/SortData";

const CustomTooltip = ({ active, payload, SufixPrefix }) => {
  if (active && payload && payload.length && SufixPrefix) {
    // console.log(payload);
    const data = payload[0].payload;
    // console.log(data);

    const dataKeys = Object.keys(data);
    const stringKeys = Object.keys(data)[0];
    const numberKeys = Object.keys(data)[1];
    // console.log(data[numberKeys]);
    const { decimalPlaces, factor, prefix, sufix, mode } = SufixPrefix;
    // console.log(decimalPlaces, factor);
    // formatNumber(data[numberKeys],SufixPrefix)
    return (
      <div
        className="custom-tooltip border border-gray-200 rounded shadow-xl bg-white p-[10px]"
        style={{
          background: "white",
          padding: "10px",
        }}
      >
        {SufixPrefix && prefix !== undefined && sufix !== undefined ? (
          <button className="w-auto flex flex-col items-start justify-start text-black font-light mb-1">
            {`${stringKeys} : ${data[stringKeys]}`}
            <p className="font-[500]">
              {`${numberKeys} : ${formatNumber(data[numberKeys], SufixPrefix)}`}
            </p>
          </button>
        ) : (
          <button className="w-auto flex flex-col items-start justify-start text-black font-light mb-1">
            {`${stringKeys} : ${data[stringKeys]}`}
            <p className="font-[500]">{`${numberKeys} : ${formatNumber(
              data[numberKeys],
              SufixPrefix
            )}`}</p>
          </button>
        )}
      </div>
    );
  }

  return null;
};
// const xa = rawRowkeys[0];
// const yb = valueKeys[0];
// const SufixPrefix = { ...valueKeys.map((item) => item.numberFormat)[0] };

const CustomRectangle = (props) => {
  const { x, y, width, height, fill, index, activeIndex } = props;
  const isActive = activeIndex.includes(index); // Check if the current index is in activeIndex

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      stroke={isActive ? "black" : "none"} // Add black stroke if the index is active
      strokeWidth={isActive ? 2 : 0} // Optional: thicker stroke for active bars
    />
  );
};

const BarChartComp = ({
  Data,
  Sheetid,
  rowKeys: rawRowkeys,
  valueKeys,
  toolTipKeys,
  groupByKeys,
  sortProperties,
}) => {
  const xa = rawRowkeys[0];
  const yb = valueKeys[0];
  const xAxisCaption = xa?.responseKey; // Access caption value for X axis
  const yAxisCaption = yb?.caption; // Access caption value for Y axis

  const rowKeys = rawRowkeys.map((item) => item.responseKey);
  const SufixPrefix = { ...valueKeys.map((item) => item.numberFormat)[0] };
  // console.log(SufixPrefix.mode);
  // console.log(Data);

  // Loop through the data to find the longest number's length

  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );

  const isAnimataionOff = useSelector(
    getSheetControls({ Sheetid: Sheetid, key: "isAnimataionOff" })
  );

  const dispatch = useDispatch();
  const [clickedCaption, setClickedCaption] = useState([]);
  const [activeIndex, setActiveIndex] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);
  const nanoId = nanoid();

  const handleClicked = (data, index, event) => {
    // Find the clicked value dynamically based on the key that includes 'Date'
    // console.log(data);

    let clickedValue;
    for (let key in data) {
      if (data[key] !== null && data[key] !== undefined) {
        clickedValue = data[key];
        break;
      }
    }
    // console.log(clickedValue);

    const isCtrlPressed = event.ctrlKey || event.metaKey;
    // console.log(isCtrlPressed);

    setClickedCaption((prevCaption) => {
      let newCaption;

      if (isCtrlPressed && prevCaption !== undefined) {
        // Check if clickedValue is already in prevCaption
        if (prevCaption.includes(clickedValue)) {
          // Remove the clickedValue from prevCaption
          newCaption = prevCaption.filter((item) => item !== clickedValue);
        } else {
          // Add the clickedValue to prevCaption
          newCaption = [...prevCaption, clickedValue];
        }
      } else {
        // If Ctrl is not pressed, set clickedCaption to just clickedValue
        newCaption = [clickedValue];
      }

      const { columnName, dataType, uuid, caption, format } = rawRowkeys[0];
      // console.log(rawRowkeys[0]);
      // console.log(format);
      if (
        (Array.isArray(activeIndex) &&
          activeIndex.includes(index) &&
          newCaption.length === 0) ||
        activeIndex[0] === hoverIndex
      ) {
        // console.log("remove");
        dispatch(
          deleteTemporaryFilters({
            sourceSheet: Sheetid,
            allTempFiltersBoolean: false,
          })
        );
      } else {
        // console.log("add");
        if (newCaption.length) {
          const filterObject = {
            columnName,
            rename: caption,
            dataType,
            uuid,
            selectedSheetId: Sheetid,
            selectedOperator: "IN",
            selectedFilterValues: newCaption,
            nanoId: nanoId,
            filterType: "temporary",
            ...(dataType === "Date" ? { format: format } : {}), // Conditionally add the format property
          };
          // console.log(filterObject);
          dispatch(setFiltersToOtherSheets(filterObject));
        } else {
          // console.log("newCaption is empty");
        }
      }
      return newCaption;
    });

    setActiveIndex((prevIndices) => {
      let newIndices;

      if (isCtrlPressed && prevIndices !== undefined) {
        // Check if the index is already in prevIndices
        if (prevIndices.includes(index)) {
          // Remove the index from prevIndices
          newIndices = prevIndices.filter((i) => i !== index);
        } else {
          // Add the index to prevIndices
          newIndices = [...prevIndices, index];
        }
      } else {
        // If Ctrl is not pressed, set activeIndex to just the clicked index
        newIndices = [index];
      }

      // console.log(newIndices);
      return newIndices;
    });
  };

  const handleMouseEnter = (data, index, event) => {
    setHoverIndex(index);
  };
  const handleMouseLeave = () => {
    setHoverIndex(null);
  };

  const COLORS = getColourSchemeByID(customizationObject?.colorSchemeid).colors;
  const barSize = customizationObject.barsize;
  const stackGap = customizationObject.stackGap;
  const referenceLine = customizationObject.referenceLine;
  const isLabelVisible = customizationObject.isLabelVisible;
  const labelPosition = customizationObject.labelPosition;
  const barlabelFontSize = customizationObject.barlabelFontSize;
  const labelFontColor = customizationObject.labelFontColor;
  const labelRotate = customizationObject.labelRotate;
  const isGridVisible = customizationObject.isGridVisible;
  const isTransposeVisible = customizationObject.isTransposeVisible;
  const { isAxeslabelVisible } = customizationObject;
  const axeslabelFontSize = customizationObject.axeslabelFontSize;
  // console.log(isTransposeVisible);
  // console.log(labelRotate);
  // console.log(axeslabelFontSize);

  const getColor = (index) => {
    return rowKeys.length === 1 ? COLORS[0] : COLORS[index % COLORS.length];
  };
  // console.log(barlabelFontSize);

  // const tickFormatter = (tick) => {
  //   // // console.log(tick);

  //   if (typeof tick === "number") {
  //     // // console.log(formatNumber(tick, SufixPrefix.mode));

  //     return formatNumber(tick, SufixPrefix);
  //   }
  //   return tick;
  // };

  const tickFormatter = (tick) => {
    if (typeof tick === "number") {
      return formatNumber(tick, SufixPrefix);
    }
    return tick;
  };

  const left = isAxeslabelVisible
    ? 50
    : isTransposeVisible
      ? 20
      : SufixPrefix.mode && SufixPrefix.mode !== "Auto"
        ? 4
        : 5;
  const top = isAxeslabelVisible
    ? 15
    : isTransposeVisible
      ? 15
      : SufixPrefix.mode && SufixPrefix.mode !== "Auto"
        ? 2
        : 20;
  const bottom = isAxeslabelVisible
    ? 40
    : isTransposeVisible
      ? 20
      : SufixPrefix.mode && SufixPrefix.mode !== "Auto"
        ? 4
        : 6;
  const CustomLabel = ({ x, y, value, labelRotate }) => {
    return (
      <text
        x={x}
        y={y - 280} // Move label up by 280 pixels
        textAnchor="middle"
        fill={labelFontColor}
        fontSize={barlabelFontSize}
        transform={`rotate(${labelRotate}, ${x}, ${y - 280})`} // Apply rotation around the label's position
      >
        {value}
      </text>
    );
  };

  const data = sortData({
    transformedData: Data,
    sortProperties,
    rawRowKeys: rawRowkeys,
    rawGroupByKeys: groupByKeys,
  });
  // console.log(data);



  return (
    <ResponsiveContainer width="100%" height="100%">

      <BarChart
        data={data}
        layout={isTransposeVisible ? "vertical" : "horizontal"}
        margin={{
          left: left,
          bottom: bottom,
          top: top,
          right: 0,
        }}
      >
        <XAxis
          type={isTransposeVisible ? "number" : "category"}
          dataKey={isTransposeVisible ? null : rowKeys[0]}
          tick={{ fill: "#75777F" }}
          tickFormatter={isTransposeVisible ? tickFormatter : null}
          label={
            isAxeslabelVisible
              ? {
                content: ({ viewBox }) => {
                  const { x, y, width, height } = viewBox;
                  const centerX = x + width / 2;
                  const centerY = y + height / 2;

                  return (
                    <text
                      x={centerX}
                      y={isTransposeVisible ? 566 : y + 50}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={labelFontColor}
                      style={{
                        fontWeight: "normal",
                        fontSize: axeslabelFontSize,
                      }}
                      transform={
                        isTransposeVisible
                          ? null
                          : `rotate(0, ${centerX}, ${566})`
                      }
                    >
                      {isTransposeVisible ? yAxisCaption : xAxisCaption}
                    </text>
                  );
                },
              }
              : null
          }
        />
        <YAxis
          type={isTransposeVisible ? "category" : "number"}
          dataKey={isTransposeVisible ? rowKeys[0] : null}
          tick={{ fill: "#75777F" }}
          tickFormatter={tickFormatter}
          label={
            isAxeslabelVisible
              ? {
                content: ({ viewBox }) => {
                  const { x, y, height } = viewBox;
                  const adjustedX = isTransposeVisible ? x - 30 : x - 50;
                  const adjustedY = isTransposeVisible ? 250 : y + height / 2;

                  return (
                    <text
                      x={adjustedX}
                      y={adjustedY}
                      textAnchor="middle"
                      dominantBaseline={
                        isTransposeVisible ? "middle" : "hanging"
                      }
                      fill={labelFontColor}
                      style={{
                        fontWeight: "normal",
                        fontSize: axeslabelFontSize,
                      }}
                      transform={
                        isTransposeVisible
                          ? `rotate(-45, ${adjustedX}, 250)`
                          : `rotate(-90, ${adjustedX}, ${adjustedY})`
                      }
                    >
                      {isTransposeVisible ? xAxisCaption : yAxisCaption}
                    </text>
                  );
                },
              }
              : null
          }
        />

        {isGridVisible && <CartesianGrid strokeDasharray="3 3" />}
        <ReferenceLine
          x={isTransposeVisible ? referenceLine : null}
          y={isTransposeVisible ? null : referenceLine}
        />
        <Tooltip
          content={
            <CustomTooltip
              toolTipKeys={toolTipKeys}
              SufixPrefix={SufixPrefix}
            />
          }
        />
        <Bar
          dataKey={valueKeys[0]?.responseKey}
          fill="#4a067d"
          barSize={barSize}
          activeBar={<Rectangle stroke="black" />}
          activeIndex={activeIndex}
          name={"sa"}
          shape={(props) => (
            <CustomRectangle {...props} activeIndex={activeIndex} />
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClicked}
          isAnimationActive={isAnimataionOff}
        >
          {Data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(index)} />
          ))}
          {isLabelVisible && (
            <LabelList
              dataKey={valueKeys[0]?.responseKey}
              position={labelPosition.value}
              fontSize={barlabelFontSize}
              fill={labelFontColor}
              stroke={labelFontColor}
              angle={labelRotate}
              offset={12}
              strokeWidth={0.2}
              dominantBaseline={
                labelPosition?.value === "top"
                  ? "middle"
                  : labelPosition?.value === "center"
                    ? "center"
                    : null
              }
              formatter={(value) => formatNumber(value, SufixPrefix)}
              style={{ fontWeight: "800" }}
            />
          )}
        </Bar>
      </BarChart>
    </ResponsiveContainer>

  );
};

export default BarChartComp;
